import { dateTime } from "./format";

// The words the payment panel uses for what the bank said, kept in one place
// (docs/ui-standard.md §8). The API sends codes; staff read sentences.

// Who confirmed a receipt (payment.records[].confirmed_by), as a sentence.
const CONFIRMED_BY = {
  customer: "Confirmed by the customer's return from the bank",
  staff: "Confirmed by a staff check",
  check: "Found by a bank check when the customer reopened the application",
};

export const confirmedByText = (by) => CONFIRMED_BY[by] || "How it was confirmed was not recorded";

// What the bank said about one opening of the payment page
// (payment.attempts[].outcome). null means nobody has asked the bank yet.
const OUTCOME = {
  paid: { label: "Paid", tone: "success" },
  declined: { label: "Declined by the bank", tone: "error" },
  pending: { label: "Waiting for the bank", tone: "warning" },
  mismatch: { label: "Amount didn't match", tone: "error" },
  refunded: { label: "Refunded", tone: "default" },
  not_started: { label: "No payment made", tone: "default" },
};

const NOT_CHECKED = { label: "Not checked yet", tone: "default" };

export const outcome = (code) => (code == null ? NOT_CHECKED : OUTCOME[code] || { label: "Unknown answer from the bank", tone: "default" });

export const outcomeText = (code) => outcome(code).label;

// "$300", "€90", "LBP 1,000": the amount in its own currency (USD when the
// API sends none).
export function moneyIn(amount, currency = "USD") {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "USD", maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  }
}

// "Mastercard ••0008", "Card ••0008", "Mastercard", or "" when the bank gave
// no card details.
export function cardText(brand, last4) {
  const name = brand ? String(brand).trim() : "";
  const end = last4 ? String(last4).trim() : "";
  if (end) return `${name || "Card"} ••${end}`;
  return name;
}

// One receipt as the panel prints it.
export function receiptLines(record) {
  return {
    number: record.receipt_number || "No receipt number",
    amount: moneyIn(record.amount, record.currency),
    card: cardText(record.card_brand, record.card_last4),
    paidAt: dateTime(record.paid_at || record.created_at),
    confirmedBy: confirmedByText(record.confirmed_by),
  };
}

// What the panel says when the lists are empty (before migration 011, or
// nothing recorded yet). null when there is something to list.
export function emptyText({ paid, records, attempts, billedTo }) {
  if (paid && !records.length) return "Paid (no receipt details recorded)";
  if (!paid && !attempts.length && !billedTo) return "The customer hasn't opened the payment page yet";
  return null;
}
